import {
    Component,
    ChangeDetectionStrategy,
    Input,
    Type,
    Output,
    EventEmitter,
    HostListener,
    OnInit,
    AfterViewInit,
    HostBinding,
} from '@angular/core';
import { MapItemComponent, MapItem } from '@ui/shared/util-map';
import { Result } from '@team23/ts-result';
import { FilterModel } from '@ui/shared/util-filter';
import { isMobile } from '@ui/shared/ui-common';
import { ApiDataStatus, CoordinateDTO } from '@ui/shared/util-api';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-map-with-sidebar',
    templateUrl: './map-with-sidebar.component.html',
    styleUrls: ['./map-with-sidebar.component.scss'],
    standalone: false,
})
export class MapWithSidebarComponent<MapItemType extends MapItem> implements OnInit, AfterViewInit {
    @Input()
    public categories?: FilterModel | undefined;

    @Input({ required: true })
    public popupContentComponentType!: Type<MapItemComponent<MapItemType>>;

    @Input()
    public listCustomContentComponentType?: Type<MapItemComponent<MapItemType>> | undefined;

    @Input({ required: true })
    public mapItems!: Result<Array<MapItemType>, ApiDataStatus>;

    @Input()
    public mapAreas?: Result<Array<Array<CoordinateDTO>>, ApiDataStatus> | null;

    @Output()
    public readonly reloadMapData = new EventEmitter<void>();

    @HostBinding('class.fullscreen')
    public isFullscreen = false;

    public isMobile = false;

    public loadingStatus = ApiDataStatus.Loading;

    public sidebarIsMinimized = false;

    public showMap = true;

    public showMapItemsMobile = false;

    public get isMapDataErrored(): boolean {
        return !!(this.mapItems.error && this.mapItems.error.key !== ApiDataStatus.Loading)
            || !!(this.mapAreas?.error && this.mapAreas.error.key !== ApiDataStatus.Loading);
    }

    @HostListener('window:resize')
    public onResize(): void {
        this.isMobile = isMobile();

        // Set the fullscreen viewport height whenever the window resizes.
        this._setFullscreenHeight();
    }

    @HostListener('document:fullscreenchange')
    public onFullscreenChange(): void {
        this.isFullscreen = !!document.fullscreenElement;
    }

    public triggerMapDataReload(): void {
        this.reloadMapData.emit();
    }

    public onlyShowMap(): void {
        this.showMap = true;
        this.showMapItemsMobile = false;
    }

    public ngOnInit(): void {
        this.onResize();
    }

    public ngAfterViewInit(): void {
        this.onResize();
    }

    public onlyShowMapItemsMobile(): void {
        this.showMap = false;
        this.showMapItemsMobile = true;
    }

    /**
     * Sets the ideal view height for certain page elements in fullscreen.
     * The smaller value of `screen.height` and `window.innerheight` should be safe for most devices.
     *
     * This is intended to prevent overlaps and cutoffs in fullscreen on mobile, where the available space is unreliable.
     */
    private _setFullscreenHeight(): void {
        const htmlElement = document.querySelector<HTMLElement>('#map-with-sidebar');

        const availableHeights = [
            screen.height,
            window.innerHeight,
        ]
            .filter(height => height > 0);

        if (availableHeights.length) {
            const safeHeight = Math.min(...availableHeights);

            if (htmlElement) {
                htmlElement.style.setProperty('--fullscreen-safe-height', `${ safeHeight }px`);
            }
        }
    }
}
